import { AppIcon } from '@capacitor-community/app-icon';
import { isPlatform } from '@ionic/react';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { usePrevious } from 'react-use';
import { useAppState } from '../../api/hooks/app-state.hook';
import {
  TenantPreference,
  useGetTenantPreference,
  useSetTenantPreferenceLazy,
} from '../../api/hooks/preference.hooks';
import { useObservableCacheItem } from '../../cache/observable-cache-hooks';
import { useObservableCache } from '../../cache/observable-cache-provider';
import {
  Branding,
  DefaultPartner,
  Partner,
  Partners,
  Theme,
} from '../../config/branding';
import { logger } from '../../util/logger';

const PARTNER_CACHE_KEY = 'partner';

type SetPartnerLazyCallback = (name: string) => Promise<void>;

interface AppIconContextValue {
  sync: (name: string) => Promise<void>;
}

interface AppIconContextProps {
  children: ReactNode;
}

const AppIconContext = createContext<AppIconContextValue | undefined>(
  undefined
);

const AppIconContextProvider = (props: AppIconContextProps) => {
  const { children } = props;

  const partner = useGetLocalPartner();
  const previousPartnerName = usePrevious(partner?.name);

  const sync = useCallback(
    async (name: string, suppressNotification = true): Promise<void> => {
      if (isPlatform('capacitor')) {
        // filter down to partner names that we should disable. our
        // default partner (beacon) does not need to be included in this
        // list since it only applies to Android and we aren't registering
        // the default as a new activity alias
        const disable = Partners.filter(
          (p) => p.name !== name && p.name !== DefaultPartner.name
        ).map((p) => p.name);

        const isSupported = await AppIcon.isSupported();
        const isRequestingDefaultIcon = name === DefaultPartner.name;

        if (isSupported.value) {
          const current = await AppIcon.getName();

          if (isRequestingDefaultIcon) {
            if (current.value !== null) {
              logger.info('resetting app icon');
              AppIcon.reset({
                suppressNotification,
                disable,
              });
            }
          } else if (name !== current.value) {
            logger.info('changing app icon', {
              name,
            });

            AppIcon.change({
              name,
              suppressNotification,
              disable,
            });
          }
        }
      }
    },
    []
  );

  useEffect(() => {
    // for iOS we can auto sync the app icon without the app being
    // forced into the background. for Android we wait until the
    // app is going into the background before syncing
    if (isPlatform('ios')) {
      if (
        partner?.name !== undefined &&
        previousPartnerName !== undefined &&
        partner?.name !== previousPartnerName
      ) {
        logger.info('auto syncing app icon for ios', {
          name: partner.name,
        });
        sync(partner.name);
      }
    }
  }, [partner, previousPartnerName, sync]);

  useAppState({
    background: () => {
      if (partner?.name) {
        // sync icon when moving to background. this works for Android, but iOS
        // cancels the operation. with Android the app gets put into the background
        // when the icon changes so we don't want to optimistically take this action
        if (isPlatform('android')) {
          sync(partner?.name);
        }
      }
    },
  });

  const value = useMemo<AppIconContextValue>(() => {
    return {
      sync,
    };
  }, [sync]);

  return (
    <AppIconContext.Provider value={value}>{children}</AppIconContext.Provider>
  );
};

const useAppIconContext = (): AppIconContextValue => {
  return useContext(AppIconContext) as AppIconContextValue;
};

const useGetPartner = (): Partner => {
  const cache = useObservableCache();

  const partnerPreference = useGetTenantPreference<string>(
    TenantPreference.PARTNER
  );

  const partner = useMemo<Partner>(() => {
    return (
      Partners.find((p) => p.name === partnerPreference?.value) ??
      DefaultPartner
    );
  }, [partnerPreference]);

  const localPartner = useGetLocalPartner();

  useEffect(() => {
    // write the partner to the local cache which will
    // go ahead and trigger a css change
    if (partner.name !== localPartner?.name) {
      cache.setItem(PARTNER_CACHE_KEY, partner.name);
    }
  }, [partner, localPartner, cache]);

  return partner;
};

const useSetPartnerLazy = (): SetPartnerLazyCallback => {
  const [savePreference] = useSetTenantPreferenceLazy();
  const cache = useObservableCache();

  const appIcon = useAppIconContext();

  return useCallback<SetPartnerLazyCallback>(
    async (name: string) => {
      logger.info('setting partner', {
        name,
      });

      await savePreference({
        key: TenantPreference.PARTNER,
        value: name,
      });

      cache.setItem(PARTNER_CACHE_KEY, name);
      await appIcon.sync(name);
    },
    [savePreference, appIcon, cache]
  );
};

const useGetLocalPartner = (): Partner | undefined => {
  const partnerName = useObservableCacheItem<string>(PARTNER_CACHE_KEY);

  const partner = useMemo(() => {
    return Partners.find((p) => p.name === partnerName);
  }, [partnerName]);

  return partner;
};

const useGetBranding = (): Branding | undefined => {
  return useGetLocalPartner()?.branding;
};

const useGetForcedColorScheme = (): Theme | undefined => {
  return useGetBranding()?.forceColorScheme;
};

const useHasForcedColorScheme = (): boolean => {
  return useGetForcedColorScheme() !== undefined;
};

export {
  AppIconContextProvider,
  useAppIconContext,
  useGetBranding,
  useGetForcedColorScheme,
  useGetPartner,
  useHasForcedColorScheme,
  useSetPartnerLazy,
};
